"use client";

import { ArrowDown, Check, ChevronRight, Play, Sparkles } from "lucide-react";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { Button } from "@/components/ui/button";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import type { PlatformId } from "@/types/platforms";

const HERO_PLATFORMS: PlatformId[] = [
  "youtube",
  "tiktok",
  "instagram",
  "facebook",
  "linkedin",
  "x",
];

export function Hero() {
  const { t } = useJsonTranslation("Hero");

  return (
    <div
      data-home-block="hero"
      className="relative flex min-h-[62vh] flex-col items-center justify-center overflow-hidden px-1 pt-20 pb-4 sm:pt-24"
    >
      {/* Archival glass field: subtle grid, not a heavy decorative fill. */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="home-hero-ambient absolute inset-0" aria-hidden="true" />
        <div
          className="hero-grid absolute inset-0 opacity-55"
          aria-hidden="true"
        />
      </div>

      <div className="relative z-10 mx-auto max-w-5xl text-center">
        {/* Live badge — single clean signal */}
        <div className="home-badge mb-7">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="bg-leaf-400 absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" />
            <span className="bg-leaf-500 relative inline-flex h-2 w-2 rounded-full" />
          </span>
          <span>{t("home.hero.badge")}</span>
        </div>

        {/* Main headline */}
        <h1 className="font-home-display mb-5 text-4xl leading-[0.98] font-semibold text-balance sm:text-6xl lg:text-7xl xl:text-[5rem]">
          <span className="text-foreground block">
            {t("home.hero.headline1")}
          </span>
          <span className="text-gradient-primary mt-1 block">
            {t("home.hero.headline2")}
          </span>
        </h1>

        {/* Subheadline — one clear sentence */}
        <p className="home-section-copy mx-auto mt-6 max-w-2xl text-lg text-pretty sm:text-xl">
          {t("home.hero.subheadline")}
        </p>

        {/* 6-platform preview strip — concrete anchor for "6 platforms" claim */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <span className="home-eyebrow mr-1">
            {t("home.hero.optimizedFor")}
          </span>
          {HERO_PLATFORMS.map((p) => (
            <div
              key={p}
              className="home-chip group flex h-10 w-10 items-center justify-center p-0 transition-all duration-300 hover:-translate-y-0.5"
              title={p}
              aria-label={p}
            >
              <PlatformLogo
                platform={p}
                size={22}
                className="opacity-85 transition-opacity duration-300 group-hover:opacity-100"
              />
            </div>
          ))}
        </div>

        {/* CTA row — single primary + secondary scroll link */}
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-5">
          <Button
            asChild
            variant="outline"
            size="xl"
            className="home-outline-action group"
          >
            <Link href="/signup">
              <Play className="h-5 w-5 fill-current" aria-hidden="true" />
              {t("home.hero.cta")}
              <ChevronRight
                className="h-4 w-4 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
          </Button>
          <a
            href="#live-demo"
            className="home-ghost-link group inline-flex min-h-[44px] items-center gap-2"
          >
            <span className="underline-offset-4 group-hover:underline">
              {t("home.hero.ctaSecondary")}
            </span>
            <ArrowDown
              className="h-4 w-4 animate-bounce transition-transform"
              aria-hidden="true"
            />
          </a>
        </div>

        {/* Trust row — reduced to 3 signals, subtle, one line */}
        <div className="text-muted-foreground/70 mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm">
          <span className="inline-flex items-center gap-1.5">
            <Check
              className="text-leaf-500 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            {t("home.hero.trustSignal1")}
          </span>
          <span
            className="text-foreground/20 hidden sm:inline"
            aria-hidden="true"
          >
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Check
              className="text-leaf-500 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            {t("home.hero.trustSignal2")}
          </span>
          <span
            className="text-foreground/20 hidden sm:inline"
            aria-hidden="true"
          >
            ·
          </span>
          <span className="text-leaf-600 dark:text-leaf-400 inline-flex items-center gap-1.5 font-medium">
            <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t("home.hero.moneyBack")}
          </span>
        </div>
      </div>
    </div>
  );
}
